import { useState } from 'react';
import { closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { reorderTodo } from '../../moduleServices/planner_todos_api';
import { toast } from 'react-hot-toast';
import { useResponsiveDrag } from 'services/useResponsiveDrag';
import { useDevice } from 'services/DeviceContext';

// ##### Custom hook for todo drag and drop functionality
export const useTodoDrag = (todo, setTodo) => {
  const [activeTodoId, setActiveTodoId] = useState(null);
  const { 
    activationDistance, 
    activationDelay, 
    activationTolerance, 
    enableTouchGestures 
  } = useResponsiveDrag();
  
  const { isMobile } = useDevice();

  // ##### RESPONSIVE SENSORS CONFIGURATION
  const pointerSensor = useSensor(PointerSensor, { 
    activationConstraint: { distance: activationDistance } 
  });
  
  const touchSensor = useSensor(TouchSensor, { 
    activationConstraint: { 
      delay: activationDelay, 
      tolerance: activationTolerance 
    } 
  });
  
  const sensors = useSensors(
    pointerSensor,
    ...(enableTouchGestures ? [touchSensor] : [])
  );
  
  console.log('🔧 Todo drag sensors configured:', { 
    activationDistance, 
    activationDelay, 
    activationTolerance, 
    enableTouchGestures,
    sensorsCount: sensors.length 
  });

  // ##### Handle drag start
  const handleDragStart = (event) => {
    console.log('🎯 Todo drag started:', event.active.id);
    setActiveTodoId(event.active.id);
  };

  // ##### Recalculate todo orders after drag
  const recalculateTaskOrders = (activeTodo, overTodo, newColumn) => {
    const todoCopy = [...todo];
    
    console.log('🔄 Recalculating orders:', { activeTodo: activeTodo.id, overTodo: overTodo.id, newColumn });
    
    // Get all todo from target column (including the moved todo)
    const targetColumnTodo = todoCopy.filter(t => t.column === newColumn);
    targetColumnTodo.sort((a, b) => a.order - b.order);
    
    console.log('📋 Target column todo before reorder:', targetColumnTodo.map(t => ({ id: t.id, order: t.order })));
    
    // If moving within the same column, use arrayMove like todo items
    if (activeTodo.column === newColumn) {
      const oldIndex = targetColumnTodo.findIndex(t => t.id === activeTodo.id);
      const newIndex = targetColumnTodo.findIndex(t => t.id === overTodo.id);
      
      console.log('🔄 Same column reorder:', { oldIndex, newIndex });
      
      // Use arrayMove for correct positioning
      const reorderedTodo = arrayMove(targetColumnTodo, oldIndex, newIndex);
      
      // Update orders
      reorderedTodo.forEach((todo, index) => {
        todo.order = index;
      });
      
      console.log('📋 Reordered todo:', reorderedTodo.map(t => ({ id: t.id, order: t.order })));
      
      // Update the main todoCopy array
      reorderedTodo.forEach(modifiedTodo => {
        const index = todoCopy.findIndex(t => t.id === modifiedTodo.id);
        if (index !== -1) {
          todoCopy[index] = modifiedTodo;
        }
      });
    } else {
      // Moving between columns - insert after the over todo
      console.log('🔄 Moving between columns');
      
      const activeIndex = todoCopy.findIndex(t => t.id === activeTodo.id);
      const [movedTodo] = todoCopy.splice(activeIndex, 1);
      
      // Update column
      movedTodo.column = newColumn;
      
      // Get target column todo (excluding the moved todo)
      const targetTodo = todoCopy.filter(t => t.column === newColumn);
      targetTodo.sort((a, b) => a.order - b.order);
      
      console.log('📋 Target todo before insert:', targetTodo.map(t => ({ id: t.id, order: t.order })));
      
      // Insert after the over todo
      const overIndex = targetTodo.findIndex(t => t.id === overTodo.id);
      targetTodo.splice(overIndex + 1, 0, movedTodo);
      
      console.log('📋 Target todo after insert:', targetTodo.map(t => ({ id: t.id, order: t.order })));
      
      // Update orders
      targetTodo.forEach((todo, index) => {
        todo.order = index;
      });
      
      // Update the main todoCopy array
      targetTodo.forEach(modifiedTodo => {
        const index = todoCopy.findIndex(t => t.id === modifiedTodo.id);
        if (index !== -1) {
          todoCopy[index] = modifiedTodo;
        } else {
          // Add the moved todo
          todoCopy.push(modifiedTodo);
        }
      });
    }
    
    return todoCopy;
  };

  // ##### Mobile-specific todo reordering (stacked columns)
  const reorderTodoMobile = (activeTodo, overTodo) => {
    const todoCopy = [...todo];
    
    console.log('📱 Mobile reorder:', { activeTodo: activeTodo.id, overTodo: overTodo.id });
    
    // Get all todo sorted by column then order (stacked)
    const allTodo = todoCopy.sort((a, b) => {
      if (a.column !== b.column) {
        return a.column - b.column; // Column 0 first, then column 1
      }
      return a.order - b.order; // Within each column, sort by order
    });
    
    console.log('📋 All todo before reorder:', allTodo.map(t => ({ id: t.id, column: t.column, order: t.order })));
    
    // Find indices in the stacked array
    const oldIndex = allTodo.findIndex(t => t.id === activeTodo.id);
    const newIndex = allTodo.findIndex(t => t.id === overTodo.id);
    
    console.log('🔄 Mobile reorder indices:', { oldIndex, newIndex });
    
    // Use arrayMove on the stacked array
    const reorderedTodo = arrayMove(allTodo, oldIndex, newIndex);
    
    // Recalculate orders and columns based on new position
    reorderedTodo.forEach((todo, index) => {
      // Determine new column and order based on position in stacked array
      const totalColumn0Tasks = todoCopy.filter(t => t.column === 0).length;
      
      if (index < totalColumn0Tasks) {
        // First half goes to column 0
        todo.column = 0;
        todo.order = index;
      } else {
        // Second half goes to column 1
        todo.column = 1;
        todo.order = index - totalColumn0Tasks;
      }
    });
    
    console.log('📋 Reordered todo:', reorderedTodo.map(t => ({ id: t.id, column: t.column, order: t.order })));
    
    return reorderedTodo;
  };

  // ##### Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTodoId(null);
    
    console.log('handleTodoDragEnd called:', { active: active.id, over: over?.id });
    
    const activeTodo = todo.find(t => t.id === active.id);
    if (!activeTodo) return;
    
    // Check if dropping on drop zones
    if (over?.id === 'left-column') {
      console.log('Dropping todo in left column');
      
      // Move todo to left column
      const updatedTodo = recalculateTaskOrders(activeTodo, { column: 0, order: 0 }, 0);
      
      // Optimistic update
      setTodo(updatedTodo);
      
      // API call
      try {
        await reorderTodo(updatedTodo);
        console.log('Todo moved to left column successfully');
      } catch (error) {
        console.error('Error moving todo to left column:', error);
        toast.error('Failed to move todo. Please try again.', { closeButton: true });
        // Revert optimistic update
        setTodo(todo);
      }
      return;
    }
    
    if (over?.id === 'right-column') {
      console.log('Dropping todo in right column');
      
      // Move todo to right column
      const updatedTodo = recalculateTaskOrders(activeTodo, { column: 1, order: 0 }, 1);
      
      // Optimistic update
      setTodo(updatedTodo);
      
      // API call
      try {
        await reorderTodo(updatedTodo);
        console.log('Todo moved to right column successfully');
      } catch (error) {
        console.error('Error moving todo to right column:', error);
        toast.error('Failed to move todo. Please try again.', { closeButton: true });
        // Revert optimistic update
        setTodo(todo);
      }
      return;
    }
    
    if (!over) return;
    
    const overTodo = todo.find(t => t.id === over.id);
    
    console.log('Found todo:', { activeTodo, overTodo });
    
    if (!overTodo) return;
    
    let updatedTodo;
    
    if (isMobile) {
      // ##### MOBILE: Use stacked reordering
      updatedTodo = reorderTodoMobile(activeTodo, overTodo);
    } else {
      // ##### DESKTOP: Use column-based reordering
      const newColumn = overTodo.column;
      console.log('Moving todo to column:', newColumn);
      updatedTodo = recalculateTaskOrders(activeTodo, overTodo, newColumn);
    }
    
    console.log('Updated todo:', updatedTodo);
    
    // Optimistic update
    setTodo(updatedTodo);
    
    // API call
    try {
      await reorderTodo(updatedTodo);
      console.log('Todo reordered successfully');
    } catch (error) {
      console.error('Error reordering todo:', error);
      toast.error('Failed to reorder todo. Please try again.', { closeButton: true });
      // Revert optimistic update
      setTodo(todo);
    }
  };

  return {
    activeTodoId,
    handleDragStart,
    handleDragEnd,
    sensors
  };
};
